<script setup lang="ts">
/**
 * The blocks of one owner — a chapter's content or a math exercise's prompt: add, remove, reorder.
 * No "Add audio" — a stored audio block is still listed, reorderable and removable, but has no
 * editor. Copy that names an editor comes in by prop.
 */
import type { AssignmentContentOwner } from '../../../model/assignments/store/interfaces'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useAssignmentContentBlockList } from '../../../model/assignments/content-block-list.hook'
import ChapterBlockText from './ChapterBlockText.vue'
import ChapterBlockImage from './ChapterBlockImage.vue'

defineOptions({
  name: 'ChapterContentList',
})

const props = withDefaults(defineProps<{
  /** `{ screenId }` or `{ exerciseId }`; an `activityId` owner resolves to nothing. */
  owner: AssignmentContentOwner
  label?: string
  /** For a caller that draws its own heading. */
  hideLabel?: boolean
  /** How the stored-but-unauthorable notices name this editor. */
  editorName?: string
  textPlaceholder?: string
}>(), {
  label: 'Chapter content',
  hideLabel: false,
  editorName: 'the reading editor',
  textPlaceholder: 'The text of this chapter',
})

const {
  refBlocks,
  refHasBlocks,
  addBlock,
  removeBlock,
  moveBlockUp,
  moveBlockDown,
} = useAssignmentContentBlockList({ props })
</script>

<template>
  <div class="chapter-content-list">
    <AppText
      v-if="!hideLabel"
      name="body2-medium"
    >
      {{ label }}
    </AppText>

    <AppText
      v-if="!refHasBlocks"
      name="body2"
      color="slate-500"
    >
      No content yet.
    </AppText>

    <ul class="chapter-content-list__items">
      <li
        v-for="item in refBlocks"
        :key="item.key"
        class="chapter-content-list__item"
      >
        <div class="chapter-content-list__head">
          <AppText
            name="body2-medium"
            color="slate-500"
          >
            {{ item.block?.type ?? 'unknown' }}
          </AppText>
          <div class="chapter-content-list__actions">
            <BtnIcon
              :icon="faArrowUp"
              size="small"
              title="Move block up"
              :disabled="item.isFirst"
              @click="moveBlockUp(item.index)"
            />
            <BtnIcon
              :icon="faArrowDown"
              size="small"
              title="Move block down"
              :disabled="item.isLast"
              @click="moveBlockDown(item.index)"
            />
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              title="Remove block"
              @click="removeBlock(item.index)"
            />
          </div>
        </div>

        <ChapterBlockText
          v-if="item.block?.type === 'text'"
          :owner="owner"
          :index="item.index"
          :block="item.block"
          :placeholder="textPlaceholder"
        />
        <ChapterBlockImage
          v-else-if="item.block?.type === 'image'"
          :owner="owner"
          :index="item.index"
          :block="item.block"
        />
        <!-- A stored audio block. Read-only rather than hidden: hiding it would let an author
             remove the chapter's text and save what looks like an empty chapter while an audio
             block they never saw is still in the document (decision 5). -->
        <AppText
          v-else-if="item.block?.type === 'audio'"
          name="body2"
          color="slate-500"
        >
          This assignment carries an audio block, which {{ editorName }} does not author. It is
          kept exactly as stored. Remove it above if it does not belong.
        </AppText>
        <!-- A block this editor cannot read at all: `data` is stored unvalidated (decision 13), so
             `null`, a scalar, or a block of a type no schema version describes all arrive here.
             Listed for the same reason the audio block is — an author must be able to see, and
             remove, what is in the document — rather than crashing the update on `block.type`. -->
        <AppText
          v-else
          name="body2"
          color="slate-500"
        >
          This block is not one {{ editorName }} can open. It is kept exactly as stored. Remove it
          above if it does not belong.
        </AppText>
      </li>
    </ul>

    <div class="chapter-content-list__add">
      <BtnSecondary
        text="Add text"
        @click="addBlock('text')"
      />
      <BtnSecondary
        text="Add image"
        @click="addBlock('image')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./ChapterContentList.scss" />
